import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { Sparkles, Repeat, Coins, RotateCcw, Mail } from "lucide-react";
import { CountUpInr, CountUpRupees } from "@/components/CountUp";
import { LeadCaptureModal } from "@/components/LeadCaptureModal";
import { PERSONA_INSIGHTS } from "@/constants/data";
import { formatInr } from "@/utils/format";
import appConfig from "@/config/app.config.json";

export const ResultScreen = ({ result, selectedGoals, persona, onRestart }) => {
  const heroRef = useRef(null);
  const explainRef = useRef(null);
  const cardsRef = useRef(null);
  const insightRef = useRef(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadResolved, setLeadResolved] = useState(false); // submitted or skipped

  const showMath = appConfig?.result?.showMathInPlainEnglish === true;
  const showInsight = appConfig?.result?.showPersonaInsight !== false;
  const autoPrompt = appConfig?.result?.showLeadCaptureAutoPrompt !== false;
  const autoPromptDelay =
    Number(appConfig?.result?.leadCaptureAutoPromptDelayMs) || 1500;

  useEffect(() => {
    if (leadResolved || !autoPrompt) return;
    const t = setTimeout(() => setLeadOpen(true), autoPromptDelay);
    return () => clearTimeout(t);
  }, [leadResolved, autoPrompt, autoPromptDelay]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(heroRef.current?.children || [], {
        opacity: 0,
        y: 24,
        duration: 0.8,
        ease: "power3.out",
        stagger: 0.1,
      });
      if (explainRef.current) {
        gsap.from(explainRef.current, {
          opacity: 0,
          y: 30,
          duration: 0.7,
          delay: 0.4,
          ease: "power3.out",
        });
      }
      gsap.from(cardsRef.current?.children || [], {
        opacity: 0,
        y: 30,
        duration: 0.7,
        delay: 0.6,
        stagger: 0.12,
        ease: "power3.out",
      });
      if (insightRef.current) {
        gsap.from(insightRef.current, {
          opacity: 0,
          y: 20,
          duration: 0.6,
          delay: 1.0,
          ease: "power3.out",
        });
      }
    });
    return () => ctx.revert();
  }, []);

  const insight = PERSONA_INSIGHTS[persona?.id] || "";

  return (
    <section className="min-h-screen pt-4 md:pt-6 pb-16 px-4 sm:px-6 md:px-8 2xl:px-12 max-w-5xl 3xl:max-w-6xl mx-auto relative">
      {/* Ambient glow */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-[80vw] h-[80vw] max-w-[700px] max-h-[700px] bg-brand/[0.05] blur-[140px] rounded-full pointer-events-none -z-10" />

      {/* Hero */}
      <div ref={heroRef} className="text-center py-4 md:py-8 relative">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border-zinc-200 dark:bg-zinc-900/80 dark:border-white/10 border mb-6">
          <Sparkles className="h-3.5 w-3.5 text-brand dark:text-brand" strokeWidth={2} />
          <span className="text-xs tracking-[0.18em] uppercase text-zinc-700 dark:text-zinc-300 font-semibold">
            Your Numbers
          </span>
        </div>
        <p className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 mb-5 max-w-md mx-auto">
          To live your dream retirement in{" "}
          <span className="text-zinc-900 dark:text-zinc-100 font-medium">{result.years} years</span>,
          you will need:
        </p>
        <CountUpInr
          to={result.futureCorpus}
          duration={2}
          testId="result-corpus-amount"
          className="block font-display text-6xl sm:text-7xl md:text-8xl font-medium tracking-tighter text-zinc-900 dark:text-zinc-50 leading-none dark:drop-shadow-[0_0_30px_rgba(255,255,255,0.08)]"
          unitClassName="text-3xl sm:text-4xl md:text-6xl text-brand dark:text-brand ml-2 sm:ml-3"
        />
        <p className="text-xs md:text-sm tracking-[0.24em] uppercase text-zinc-500 dark:text-zinc-500 font-semibold mt-6">
          Future Wealth Required
        </p>
      </div>

      {/* Explanation */}
      {showMath && (
        <div
          ref={explainRef}
          data-testid="explanation-card"
          className="p-6 md:p-8 rounded-3xl bg-white/80 border-zinc-200 dark:bg-zinc-900/60 dark:border-white/[0.06] border mb-6 backdrop-blur-sm"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-500 font-semibold mb-5">
            The Math, In Plain English
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5 md:gap-6">
            <Stat
              label="Today's Lifestyle"
              value={formatInr(result.currentCorpus)}
            />
            <Stat
              label={`Future Cost · ${result.years}y`}
              value={formatInr(result.futureCorpus)}
              accent
            />
            <Stat label="Inflation" value="6.0%" />
            <Stat label="Expected Return" value="15.0%" />
          </div>
        </div>
      )}

      {/* Investment options */}
      <div
        ref={cardsRef}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 mb-8"
      >
        <InvestmentCard
          testId="sip-investment-card"
          icon={Repeat}
          eyebrow="Path 01 · Monthly"
          title="Monthly SIP Required"
          tone="brand"
        >
          <CountUpRupees
            to={result.sip}
            duration={1.8}
            delay={0.3}
            testId="sip-amount"
            className="block font-display text-4xl md:text-5xl font-medium tracking-tighter text-zinc-900 dark:text-zinc-50 leading-none"
          />
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">Per month, every month.</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-4 leading-relaxed">
            Auto-invest a fixed sum each month to reach your target.
          </p>
        </InvestmentCard>

        <InvestmentCard
          testId="lumpsum-investment-card"
          icon={Coins}
          eyebrow="Path 02 · One-Time"
          title="One-Time Investment"
          tone="white"
        >
          <CountUpInr
            to={result.lumpsumRequired}
            duration={1.8}
            delay={0.5}
            testId="lumpsum-amount"
            className="block font-display text-4xl md:text-5xl font-medium tracking-tighter text-zinc-900 dark:text-zinc-50 leading-none"
            unitClassName="text-2xl md:text-3xl text-zinc-500 dark:text-zinc-500 ml-2"
          />
          <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-2">Invest today, once.</p>
          <p className="text-xs text-zinc-500 dark:text-zinc-500 mt-4 leading-relaxed">
            A single lumpsum invested now compounding gets you to the
            exact same finish line.
          </p>
        </InvestmentCard>
      </div>

      {/* Personalized insight */}
      {showInsight && (
        <div
          ref={insightRef}
          data-testid="persona-insight"
          className="p-6 md:p-8 rounded-3xl bg-gradient-to-br from-white to-zinc-50 border-zinc-200 dark:from-zinc-900/80 dark:to-zinc-900/30 dark:border-white/10 border relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-40 h-40 bg-brand/10 blur-3xl rounded-full" />
          <p className="text-xs tracking-[0.2em] uppercase text-brand font-semibold mb-3 relative">
            For You · {persona?.label}
          </p>
          <p className="text-lg md:text-2xl text-zinc-900 dark:text-zinc-100 leading-snug tracking-tight font-medium font-display relative">
            {insight}
          </p>
          <p
            data-testid="contact-cta"
            className="text-sm md:text-base text-zinc-600 dark:text-zinc-400 mt-4 relative"
          >
            To achieve your{" "}
            <span className="text-brand font-bold">Dream Wealth Figure</span>
            ,{" "}
            <button
              type="button"
              onClick={() => setLeadOpen(true)}
              className="font-bold text-[rgb(var(--brand-secondary))] hover:underline underline-offset-4"
              data-testid="contact-cc-button"
            >
              contact Capital Canvas
            </button>
            .
          </p>
        </div>
      )}

      {/* Restart */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
        <button
          type="button"
          onClick={() => setLeadOpen(true)}
          data-testid="open-lead-modal-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-zinc-900 text-zinc-50 hover:bg-zinc-950 dark:bg-zinc-50 dark:text-zinc-950 dark:hover:bg-white text-sm font-semibold transition-all active:scale-[0.98]"
        >
          <Mail className="h-4 w-4" strokeWidth={2} />
          Request a callback
        </button>
        <button
          type="button"
          onClick={onRestart}
          data-testid="restart-button"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-zinc-300 text-zinc-600 hover:text-zinc-900 hover:border-zinc-400 dark:border-white/10 dark:text-zinc-400 dark:hover:text-zinc-50 dark:hover:border-white/25 text-sm font-medium transition-all"
        >
          <RotateCcw className="h-4 w-4" strokeWidth={2} />
          Recalculate my dream
        </button>
      </div>

      {/* Disclaimer */}
      <p className="text-[11px] text-zinc-500 dark:text-zinc-600 text-center mt-8 max-w-md mx-auto leading-relaxed">
        Indicative projections assume 6% inflation and 15% annualised returns.
        Markets vary — use this as a directional planning aid ONLY.
      </p>

      <LeadCaptureModal
        open={leadOpen}
        onClose={(reason) => {
          setLeadOpen(false);
          if (reason === "submitted" || reason === "skipped" || reason === "dismissed") {
            setLeadResolved(true);
          }
        }}
        scenario={{
          goals: selectedGoals.map((g) => ({ id: g.id, label: g.label, value: g.value })),
          years: result.years,
          persona: persona ? { id: persona.id, label: persona.label } : null,
          currentCorpus: result.currentCorpus,
          futureCorpus: result.futureCorpus,
          lumpsumRequired: result.lumpsumRequired,
          sip: result.sip,
          inflationRate: result.inflationRate,
          returnRate: result.returnRate,
        }}
      />
    </section>
  );
};

const Stat = ({ label, value, accent }) => (
  <div className="flex flex-col gap-1.5">
    <span className="text-[10px] md:text-xs tracking-[0.18em] uppercase text-zinc-500 dark:text-zinc-500 font-semibold">
      {label}
    </span>
    <span
      className={`font-display text-lg md:text-2xl font-medium tracking-tight ${
        accent ? "text-brand dark:text-brand" : "text-zinc-900 dark:text-zinc-50"
      }`}
    >
      {value}
    </span>
  </div>
);

const InvestmentCard = ({
  icon: Icon,
  eyebrow,
  title,
  children,
  tone = "white",
  testId,
}) => (
  <div
    data-testid={testId}
    className="relative p-6 md:p-7 rounded-3xl bg-white/80 border-zinc-200 dark:bg-zinc-900/80 dark:border-white/[0.08] border overflow-hidden flex flex-col"
  >
    <div
      className={`absolute -top-16 -right-16 w-48 h-48 blur-3xl rounded-full ${
        tone === "brand" ? "bg-brand/15" : "bg-zinc-100 dark:bg-white/[0.05]"
      }`}
    />
    <div className="flex items-center gap-2.5 mb-5 relative">
      <span
        className={`h-9 w-9 rounded-xl flex items-center justify-center ${
          tone === "brand"
            ? "bg-brand/15 text-brand dark:text-brand"
            : "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
        }`}
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
      </span>
      <span className="text-[11px] tracking-[0.2em] uppercase text-zinc-500 dark:text-zinc-500 font-semibold">
        {eyebrow}
      </span>
    </div>
    <h3 className="text-sm text-zinc-600 dark:text-zinc-400 mb-3 relative">{title}</h3>
    <div className="relative">{children}</div>
  </div>
);
